import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatRelative } from '@/lib/format'
import {
  useUnreadCount,
  useNotificationsList,
  useMarkRead,
  useMarkAllRead,
} from '@/hooks/useNotifications'

export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const { data: countData } = useUnreadCount()
  const unreadCount = countData?.count ?? 0

  const { data: listData, isLoading } = useNotificationsList({ page_size: 50 })
  const items = listData?.items ?? []

  const markRead = useMarkRead()
  const markAllRead = useMarkAllRead()

  // Close on click-outside
  useEffect(() => {
    if (!open) return
    function handleMouseDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [open])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open])

  function handleItemClick(notificationId: number, requestId: number | null) {
    markRead.mutate(notificationId)
    setOpen(false)
    if (requestId != null) {
      navigate(`/requests/${requestId}`)
    }
  }

  const badgeLabel = unreadCount > 99 ? '99+' : String(unreadCount)

  return (
    <div ref={containerRef} className="relative">
      {/* Bell button */}
      <button
        type="button"
        className="relative rounded-full p-2 text-slate-600 hover:bg-slate-100"
        aria-label="Notifications"
        onClick={() => setOpen((v) => !v)}
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span
            className={cn(
              'absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center',
              'rounded-full bg-red-500 px-0.5 text-[10px] font-bold leading-none text-white',
            )}
          >
            {badgeLabel}
          </span>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          className={cn(
            'absolute right-0 top-full z-50 mt-2',
            'w-[360px] rounded-lg border border-slate-200 bg-white shadow-lg',
            'flex flex-col overflow-hidden',
          )}
          style={{ maxHeight: '480px' }}
        >
          {/* Panel header */}
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <h3 className="text-sm font-semibold text-slate-900">Notifications</h3>
            {unreadCount > 0 && (
              <button
                type="button"
                className="text-xs text-wegen-600 hover:text-wegen-700 hover:underline"
                onClick={() => markAllRead.mutate()}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Panel body */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
              </div>
            ) : items.length === 0 ? (
              <p className="py-10 text-center text-sm text-slate-500">
                You&apos;re all caught up.
              </p>
            ) : (
              <ul>
                {items.map((notification) => (
                  <li key={notification.id}>
                    <button
                      type="button"
                      className={cn(
                        'flex w-full items-start gap-3 px-4 py-3 text-left transition',
                        'hover:bg-slate-50',
                        !notification.read && 'bg-blue-50/40',
                      )}
                      onClick={() => handleItemClick(notification.id, notification.request_id)}
                    >
                      {/* Unread dot */}
                      <span className="mt-1.5 shrink-0">
                        {!notification.read ? (
                          <span className="block h-1.5 w-1.5 rounded-full bg-blue-500" />
                        ) : (
                          <span className="block h-1.5 w-1.5" />
                        )}
                      </span>

                      {/* Text + timestamp */}
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm text-slate-800 leading-snug">
                          {notification.text}
                        </span>
                        <span className="mt-0.5 block text-xs text-slate-400">
                          {formatRelative(notification.created_at)}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
